import type { RuntimePluginApp } from "./runtime"
import { mergedDashboardReferences, type DashboardComponentIndex } from "./dashboard-component-index"
import { activeArtifact } from "./active-artifact"

export type AppChatPageView = {
  title: string
  description?: string
  setup?: {
    status?: string
    provider?: string
    credentials?: Array<{ key: string; label: string; configured?: boolean; verified?: boolean; required?: boolean }>
    connections?: Array<{ id: string; label: string; status: string; active?: boolean; description?: string }>
  }
  dashboard?: {
    title?: string
    description?: string
    layout?: {
      locked?: boolean
      lockedAt?: string
      lockedReason?: string
    }
    componentIndex?: DashboardComponentIndex
    defaults?: {
      system?: { id: string; label: string; scope: "system" | "user"; description?: string }
      user?: { id: string; label: string; scope: "system" | "user"; description?: string }
    }
    customization?: {
      mode?: "system" | "user" | "custom"
      activeDefaultId?: string
      hasUserDefault?: boolean
      resettable?: boolean
      updatedAt?: string
    }
  }
  blocks: Array<Record<string, unknown>>
}

export type AppChatPageState = {
  descriptor: RuntimePluginApp
  view: AppChatPageView
}

type ContextInput = {
  directory: string
  descriptor: RuntimePluginApp
  view?: AppChatPageView
  parentSessionID?: string
}

const countableKeys = [
  "rows",
  "views",
  "objects",
  "fields",
  "timeline",
  "automations",
  "jobs",
  "changes",
  "webhooks",
  "duplicates",
  "stages",
  "assets",
  "recipes",
  "recipeRuns",
  "traces",
  "studios",
  "items",
  "columns",
] as const

const statusCounts = (items: unknown) => {
  if (!Array.isArray(items)) return []
  return Object.entries(
    items.reduce<Record<string, number>>((acc, item) => {
      if (!item || typeof item !== "object" || Array.isArray(item)) return acc
      const status = (item as Record<string, unknown>).status
      if (typeof status !== "string" || !status.trim()) return acc
      return { ...acc, [status]: (acc[status] ?? 0) + 1 }
    }, {}),
  ).map(([status, count]) => `status ${status}: ${count}`)
}

const asRecord = (value: unknown) =>
  typeof value === "object" && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : {}

const asArray = (value: unknown) => (Array.isArray(value) ? value : [])

const scalarText = (value: unknown, fallback = "") => {
  if (typeof value === "string" && value.trim()) return value.trim()
  if (typeof value === "number" && Number.isFinite(value)) return String(value)
  if (typeof value === "boolean") return String(value)
  return fallback
}

const chartDetails = (block: Record<string, unknown>) => {
  if (block.type !== "chart") return []

  const data = asRecord(block.data)
  const series = asArray(data.series).flatMap((entry, index) => {
    const item = asRecord(entry)
    const key = scalarText(item.key, `series_${index + 1}`)
    const label = scalarText(item.label, key)
    const color = scalarText(item.color, "default")
    return [`series ${key} (${label}, color ${color})`]
  })

  return [
    `chart type: ${scalarText(block.chartType, "line")}`,
    `labels: ${asArray(data.labels).length}`,
    ...series,
    "Chart series colors are controlled by data.series[].color when the plugin exposes a tool or action that updates plugin-owned chart state.",
  ]
}

const registryDetails = (block: Record<string, unknown>) => {
  if (block.type !== "registry") return []
  const componentId = scalarText(block.componentId, "unknown")
  const version = scalarText(block.version, "unknown")
  return [
    `component ${componentId}.${version}`,
    typeof block.intent === "string" && block.intent.trim() ? `intent ${block.intent.trim()}` : undefined,
    typeof block.title === "string" && block.title.trim() ? `title ${block.title.trim()}` : undefined,
    `slots: ${Object.keys(asRecord(block.slots)).length}`,
    `actions: ${asArray(block.actions).length}`,
  ].filter((item): item is string => item !== undefined)
}

const blockSummary = (block: Record<string, unknown>) => {
  const id = typeof block.id === "string" ? block.id : "unknown"
  const type = typeof block.type === "string" ? block.type : "unknown"
  const counts = countableKeys.flatMap((key) => {
    const value = block[key]
    if (!Array.isArray(value)) return []
    return [`${key}: ${value.length}`, ...statusCounts(value)]
  })
  const active = typeof block.activeObject === "string" ? [`active object: ${block.activeObject}`] : []
  const pending =
    block.sync && typeof block.sync === "object" && !Array.isArray(block.sync)
      ? typeof (block.sync as Record<string, unknown>).pendingChanges === "number"
        ? [`pending changes: ${(block.sync as Record<string, unknown>).pendingChanges}`]
        : []
      : []
  const details = [...active, ...counts, ...pending, ...chartDetails(block), ...registryDetails(block)]
  if (details.length === 0) return `Block ${id}: ${type}`
  return `Block ${id}: ${type}; ${details.join("; ")}`
}

const setupSummary = (view: AppChatPageView | undefined) => {
  if (!view?.setup) return
  const credentials = (view.setup.credentials ?? []).map((credential) => {
    const state = credential.verified ? "verified" : credential.configured ? "configured" : credential.required ? "required" : "optional"
    return `${credential.label}: ${state}`
  })
  const connections = (view.setup.connections ?? []).map((connection) =>
    `${connection.label}: ${connection.status}${connection.active ? " (active)" : ""}`,
  )
  return [
    `Setup status: ${view.setup.status ?? "idle"}`,
    view.setup.provider ? `Provider: ${view.setup.provider}` : undefined,
    credentials.length ? `Credentials: ${credentials.join(", ")}` : undefined,
    connections.length ? `Connections: ${connections.join(", ")}` : undefined,
  ]
    .filter((line): line is string => line !== undefined)
    .join("\n")
}

const appContractSummary = (descriptor: RuntimePluginApp) => {
  const contract = descriptor.appContract
  if (!contract) return
  const sockets = contract.sockets ?? []
  const plugs = contract.plugs ?? []
  const orchestrates = contract.orchestrates ?? []
  return [
    `App role: ${contract.role ?? "plugin"}`,
    contract.intent ? `App intent: ${contract.intent}` : undefined,
    contract.shell?.zones?.length ? `Host shell zones: ${contract.shell.zones.join(", ")}` : undefined,
    sockets.length
      ? ["Sockets:", ...sockets.map((socket) => `- ${socket.label}: ${socket.status ?? "available"}${socket.capability ? `, ${socket.capability}` : ""}`)].join("\n")
      : undefined,
    plugs.length
      ? ["Plugs:", ...plugs.map((plug) => `- ${plug.label}: ${plug.status ?? "available"}${plug.appId ? `, app ${plug.appId}` : ""}`)].join("\n")
      : undefined,
    orchestrates.length
      ? ["Orchestrated plugins:", ...orchestrates.map((plugin) => `- ${plugin.label}: ${plugin.pluginId}`)].join("\n")
      : undefined,
  ]
    .filter((line): line is string => line !== undefined)
    .join("\n")
}

const dashboardSummary = (view: AppChatPageView | undefined) => {
  if (!view?.dashboard) return
  const references = mergedDashboardReferences(view.dashboard.componentIndex)
  const locked = Boolean(view.dashboard.layout?.locked)
  return [
    `Dashboard: ${view.dashboard.title ?? view.title}`,
    view.dashboard.description ? `Dashboard description: ${view.dashboard.description}` : undefined,
    `Dashboard layout: ${locked ? "locked" : "editable"}`,
    locked
      ? `Layout lock instruction: do not change dashboard structure, chart layout, or block order unless the user unlocks it or explicitly grants permission.`
      : undefined,
    view.dashboard.defaults?.system ? `System default: ${view.dashboard.defaults.system.label}` : undefined,
    view.dashboard.customization?.mode
      ? `Customization mode: ${view.dashboard.customization.mode}${view.dashboard.customization.activeDefaultId ? ` (${view.dashboard.customization.activeDefaultId})` : ""}`
      : undefined,
    view.dashboard.customization?.hasUserDefault ? "User custom default is available." : undefined,
    references.length
      ? [
          "Dashboard component/reference index:",
          ...references.map((reference) =>
            [
              `- ${reference.label} (${reference.kind})`,
              reference.importance ? `importance ${reference.importance}` : undefined,
              reference.path ? `path ${reference.path}` : undefined,
              reference.whenToUse ? `use ${reference.whenToUse}` : undefined,
              reference.chartTypes?.length ? `charts ${reference.chartTypes.join(", ")}` : undefined,
            ]
              .filter((item): item is string => item !== undefined)
              .join("; "),
          ),
        ].join("\n")
      : undefined,
  ]
    .filter((line): line is string => line !== undefined)
    .join("\n")
}

const ACTIVE_ARTIFACT_CAP = 9000

// When this app's right panel is the multi-artifact canvas (e.g. Dashboard), it
// publishes the focused tab via the active-artifact channel. We make the chat
// operate on THAT artifact only — the core "chat → active artifact" wiring.
const activeArtifactSummary = (descriptor: RuntimePluginApp) => {
  const a = activeArtifact()
  if (!a || a.pluginID !== descriptor.id) return // not an artifact-canvas app (or none mounted)

  if (!a.path) {
    return [
      "ACTIVE ARTIFACT: none is open right now (the empty Dashboard screen is showing).",
      `When the user asks for a publishable page/artifact, create .openanalyst/plugins/${descriptor.id}/Canvas/<kebab-title>.html. When the user asks for a multi-page site, create .openanalyst/plugins/${descriptor.id}/Canvas/<site-slug>/index.html plus sibling pages/assets. When the user asks for a local interactive utility, create .openanalyst/plugins/${descriptor.id}/Apps/<app-slug>/index.html instead; Apps are local-only and never publish.`,
    ].join("\n")
  }

  const content =
    a.content.length > ACTIVE_ARTIFACT_CAP
      ? a.content.slice(0, ACTIVE_ARTIFACT_CAP) + "\n… (truncated — read the full file before editing)"
      : a.content
  return [
    `ACTIVE ARTIFACT — the tab the user is viewing right now: "${a.name}" at plugin path "${a.path}".`,
    `When the user asks to build, edit, change, restyle, fix, or design "this" / "the artifact" / "the page" / "the canvas", operate on THIS artifact ONLY: read and write the complete document at .openanalyst/plugins/${descriptor.id}/${a.path} with core file tools or a shell script. Do NOT create a new file unless the user explicitly asks for a separate/new artifact. It re-renders in the canvas automatically after the file changes.`,
    "Current content of the active artifact:",
    "```",
    content,
    "```",
  ].join("\n")
}

export function buildAppChatSystemContext(input: ContextInput) {
  return [
    "You are OpenAnalyst inside a workspace app. You can OPERATE this plugin, not just describe it.",
    "",
    activeArtifactSummary(input.descriptor),
    "How to act on this app (file-defined):",
    `- This app is a folder bundle at .openanalyst/plugins/${input.descriptor.id}/ with Skills/*, Agents/*, Tools/* capability metadata, Scripts/* helpers, Resources/*, Views/*, Canvas/*, DB/*, memory, knowledge, context, and learnings.`,
    `- Read .openanalyst/plugins/${input.descriptor.id}/index.Md first when you need the app overview, internal system id, sections, constraints, tool names, skills, agents, scripts, credentials, or storage paths.`,
    `- For Dashboard-style apps, use Canvas/* for publishable artifacts and sites, and Apps/* for local interactive HTML/CSS/JS utilities. Apps never publish.`,
    "- Use loaded app skills first. Read app files with core read/search/edit tools when you need implementation details; do not invent or call app-specific tools that are not visible.",
    "- Run Scripts/* helpers through the core shell only when a skill or app file explicitly names the script path and inputs. External data should come from real connected MCP/core tools or the app's file/script contract.",
    `- AUTHOR this app's own capabilities by editing files directly: Skills/<name>.md, Agents/<name>.md, Tools/<name>.md metadata, and Scripts/<name> helpers under .openanalyst/plugins/${input.descriptor.id}/. From this app's chat you can only author into pluginId "${input.descriptor.id}" — never another plugin's folder.`,
    "",
    "LIVE PAGE / HTML artifact (this is the primary action for a page plugin like 10X Handle — the canvas IS the handle's actual page):",
    `- To READ the current page code, read the active Canvas/* file inside .openanalyst/plugins/${input.descriptor.id}/. ALWAYS read the real file first so you edit the REAL page, never a guess.`,
    `- To CHANGE the page (theme/colors, copy, sections, layout, "change the code"), edit that exact HTML file and write the COMPLETE updated document back to the same Canvas/* path. It re-renders in the canvas; the user can then Publish it live to the handle.`,
    "- Never reply that you cannot change the page or cannot access it: read the Canvas file, edit the HTML, and write it back through core file tools or a script. That IS how you change it.",
    "",
    `- DASHBOARD (declarative, non-page plugins): build/update by editing Canvas/*, Views/*, Resources/*, or Scripts/* files in .openanalyst/plugins/${input.descriptor.id}/. YOU author the blocks or HTML populated with the user's data.`,
    "- To MODIFY a declarative dashboard, first read the current Canvas/View/Resource file, change what the user asked, then write the full updated file back.",
    "- For fully custom design — when the user wants the elements/layout/'code' changed beyond the standard components — author a preview block { type:'preview', mode:'html', content:'<your HTML>' } so you control the exact rendered markup.",
    "- When the user asks for a sample/example dashboard, GENERATE realistic sample data yourself, design the blocks or HTML, and write the Canvas/View file so the app can render it.",
    "- Prefer real app data from its skills, scripts, resources, files, or real connected MCP/core tools; fall back to clearly-labelled sample data when none exists or the user asks for an example.",
    "",
    `App: ${input.descriptor.title}`,
    `Plugin id: ${input.descriptor.id}`,
    `Workspace: ${input.directory}`,
    input.parentSessionID ? `Backing main chat session id: ${input.parentSessionID}` : undefined,
    `Isolated folder: you are a subagent of the main project session, hard-scoped to .openanalyst/plugins/${input.descriptor.id}/ (this app's skills, agents, scripts, and files) and output/${input.descriptor.id}/. File reads and edits OUTSIDE these folders are blocked, nested subagents are blocked, and scripts should run through the core shell tool with the normal permission and safety prompts.`,
    input.descriptor.capabilities.length ? `Capabilities: ${input.descriptor.capabilities.join(", ")}` : undefined,
    appContractSummary(input.descriptor),
    input.view?.title ? `Page: ${input.view.title}` : undefined,
    input.view?.description ? `Page description: ${input.view.description}` : undefined,
    setupSummary(input.view),
    dashboardSummary(input.view),
    input.view?.blocks.length
      ? ["Visible app metadata:", ...input.view.blocks.map(blockSummary)].join("\n")
      : undefined,
    "",
    "Use this app metadata as context. Do not assume hidden record details, form values, job prompts, generated outputs, uploads, or notes are available unless the user explicitly provides them.",
    "SCOPE: this chat is BOUND to this app — it carries this app's skills, scripts, files, and context only. " +
      "When the user asks about the wider project, another app's data, or workspace-wide state (project files unrelated to this app, global settings, cross-app questions), do NOT attempt it here: " +
      `briefly tell them this is the ${input.descriptor.title} app chat and that workspace-wide questions are handled in the MAIN chat (the project chat outside this app), then offer to continue with anything for this app. ` +
      "Light context reads that this app's own work needs (its output folder, its canvas, files the user drops here) are fine.",
  ]
    .filter((line): line is string => line !== undefined)
    .join("\n")
}
